import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { createWorkerLogin, resetWorkerPassword, revokeWorkerLogin } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import Modal from '../ui/Modal';
import Field from '../ui/Field';
import ConfirmDialog from '../ui/ConfirmDialog';

// "Ravi Kumar" -> "ravi.kumar"
const suggestLogin = (w) => String(w?.name || '')
  .toLowerCase()
  .trim()
  .replace(/\s+/g, '.')
  .replace(/[^a-z0-9.]/g, '');

// Readable one-off password for handing to a worker in person.
function generatePassword() {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint32Array(10);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join('');
}

/**
 * Create, reset or revoke a worker's portal login. The login signs in on the
 * normal login page and can only reach the worker portal.
 */
export default function WorkerLoginModal({ worker, onClose, onChanged }) {
  const { success, error } = useToast();
  const [username, setUsername] = useState(() => worker.loginName || suggestLogin(worker));
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const has = !!worker.hasLogin;

  const run = async (fn, doneMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      success(res?.message || doneMsg);
      onChanged();
      onClose();
    } catch (x) {
      error(x.response?.data?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const pwOk = password.length >= 8;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Portal Login — ${worker.name}`}
      size="sm"
      footer={(
        <>
          {has && (
            <button className="btn-danger mr-auto" disabled={busy} onClick={() => setConfirmRevoke(true)}>
              Remove access
            </button>
          )}
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy || !pwOk || (!has && !username.trim())}
            onClick={() => (has
              ? run(() => resetWorkerPassword(worker.id, password), 'Password reset.')
              : run(() => createWorkerLogin(worker.id, { username: username.trim(), password }), 'Login created.'))}
          >
            {busy ? 'Saving…' : has ? 'Reset Password' : 'Create Login'}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="flex items-start gap-2 rounded-lg bg-surface-sunken px-3 py-2 text-xs text-content-muted">
          <KeyRound size={14} className="mt-0.5 shrink-0 text-brand-600" />
          {has
            ? 'This worker can sign in to their portal. Set a new password below to reset it.'
            : 'The worker signs in on the normal login page and sees only their own orders, measurements, rate card and payouts.'}
        </p>

        <Field label="Login name" required={!has}>
          <input
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={has}
            autoComplete="off"
          />
        </Field>

        <Field label={has ? 'New password' : 'Password'} required hint="At least 8 characters — share it with the worker privately">
          <div className="flex gap-2">
            <input
              className="input flex-1 font-mono"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button type="button" className="btn-outline btn-sm" onClick={() => setShow((v) => !v)}>{show ? 'Hide' : 'Show'}</button>
            <button type="button" className="btn-outline btn-sm" onClick={() => { setPassword(generatePassword()); setShow(true); }}>Generate</button>
          </div>
        </Field>
      </div>

      <ConfirmDialog
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        title="Remove portal access?"
        message={`${worker.name} will no longer be able to sign in. Their assignments and payout history are kept.`}
        confirmLabel="Remove access"
        busy={busy}
        onConfirm={() => { setConfirmRevoke(false); run(() => revokeWorkerLogin(worker.id), 'Access removed.'); }}
      />
    </Modal>
  );
}
